import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';
import type { ComponentProps, FC, ReactNode } from 'react';
import React, { Fragment, useEffect } from 'react';

import { token } from '@atlaskit/tokens';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { AnnotationsProvider } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { DocumentUpdateStatusProvider } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { FixedSpaceHeaderContainer } from '@confluence/breadcrumbs/entry-points/Breadcrumbs.tsx';
import { useInlineCommentsContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { ViewCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/ViewCommentShortcutListener.ts';
import { CompanyHubGroupedButtons } from '@confluence/company-hub/entry-points/CompanyHubGroupedButtons.ts';
import { HubPreviewFooter } from '@confluence/company-hub/entry-points/HubPreviewFooter.ts';
import { HubRendererWrapper } from '@confluence/company-hub/entry-points/hub-layout.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { ContentRefetchProvider } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForPublishedPage } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader.tsx';
import { ReadingAidsAcronymsHighlighter } from '@confluence/contextual-reading-aids/entry-points/readingAidsAcronymsHighlighter.ts';
import {
	CUSTOM_HEADER_FOOTER_TYPES,
	CustomHeaderFooterLoader,
} from '@confluence/custom-header-footer';
import { AudienceSegmentationContextProvider } from '@confluence/custom-sites-extensions/entry-points/AudienceSegmentationContext.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { getEmojiFromPublishedPageProperties } from '@confluence/emoji-title/entry-points/transformer.ts';
import { OBJECT_HEADER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import { InlineCommentsHighlighterLoader } from '@confluence/inline-comments/entry-points/InlineCommentsHighlighterLoader.ts';
import { InlineCommentsLoader } from '@confluence/inline-comments/entry-points/InlineCommentsLoader.tsx';
import { LoadingPriority } from '@confluence/loadable';
import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { usePageContentId } from '@confluence/page-context';
import { RendererPageCoverPicture } from '@confluence/page-cover-picture/entry-points/RendererPageCoverPicture.ts';
import { getRendererCoverPictureInfo } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { QuickReload } from '@confluence/quick-reload/entry-points/QuickReload.ts';
import { PageMode } from '@confluence/quick-reload/entry-points/pubSubConstants.ts';
import { RendererActionsProvider } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader.tsx';
import { useViewPageParams } from '@confluence/view-page-common/entry-points/useViewPageParams.ts';
import { ViewPageContent } from '@confluence/view-page-common/entry-points/ViewPageContent.ts';
import { ViewPageContainer } from '@confluence/view-page-common/entry-points/ViewPageContainer.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useConnectAppsData } from '@confluence/web-panel-location/entry-points/useConnectAppsData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';
import { TwgExplorerSectionLoader } from '@confluence/twg-space-visualization/entry-points/TwgExplorerSection.ts';

import { RecommendedSpaceBannerLoader as RecommendedSpaceBanner } from '../entry-points/RecommendedSpaceBanner.ts';

import { SpaceHeading } from './SpaceHeader.tsx';
import { StartSpaceOverviewPageLoad } from './perf.config.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContentBodyStyledComponent: React.ComponentType<
	{
		isCompanyHub: boolean;
	} & React.ClassAttributes<HTMLDivElement> &
		React.HTMLAttributes<HTMLDivElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div<{
	isCompanyHub: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:global': {
		/* Fixes padding for overview tab in non fallback mode. It relies on a global selector to avoid breaking theming */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.wiki-content': {
			padding: '28px 0px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		iframe: {
			marginTop: token('space.negative.250'),
		},
	},
	paddingTop: 0,
	paddingRight: `${token('space.500')}`,
	paddingBottom: `${token('space.250')}`,
	paddingLeft: `${token('space.500')}`,
	// added in for cover image
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--full-cover-image-margin': ({ isCompanyHub }) => (isCompanyHub ? '-42px' : '-40px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--full-cover-image-width': ({ isCompanyHub }) =>
		isCompanyHub ? 'calc(100% + 82px)' : 'calc(100% + 80px)',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles
	...({ isCompanyHub }) =>
		isCompanyHub && {
			paddingRight: 0,
			paddingLeft: 0,
			margin: '0 auto',
		},
});

export type SpaceOverviewProps = {
	spaceKey: string;
	children?: ReactNode;
};

const useFocusedCommentIdQuery = createSingleQueryParamHook('focusedCommentId');

export const SpaceOverview: FC<SpaceOverviewProps> = ({ spaceKey, children }) => {
	const { versionOverride } = useViewPageParams();
	const focusedCommentId = useFocusedCommentIdQuery();
	const { locale, cloudId, orgId } = useSessionData();

	// Used to clear existing comments data when we leave the space overview page
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	const { clearProcessedData } = useProcessedCommentsDataActions();

	const [contextContentId] = usePageContentId();
	const { shouldDisplaySidebar } = useInlineCommentsContext();
	const { showCommentsPanel } = useShowCommentsPanel();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId: contextContentId,
	});

	// @ts-ignore FIXME: `contextContentId` can be `undefined` here, and needs proper handling
	const contentId: string = contextContentId;

	const panelsModuleKeysAtlGeneral = useConnectAppsData({
		contentId,
		location: ATL_GENERAL,
	});
	const panelsModuleKeysAtlFooter = useConnectAppsData({
		contentId,
		location: ATL_FOOTER,
	});

	const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) && locale.startsWith('en');

	// coverPictureWidth will be cleaned up once force-fixed width flag is cleaned up
	const { coverPictureWidth } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});
	const isFixedWidth = coverPictureWidth === COVERPICTUREWIDTH.FIXED;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const isFixedWidthImageOption = !isCompanyHub && isFixedWidth;

	const spaceOverviewQueryResult = useQueryInterop<
		SpaceOverviewQuery$data,
		SpaceOverviewQuery$variables
	>(SpaceOverviewQuery, {
		variables: {
			spaceKey,
			excludeOperations: Boolean(!isCompanyHub),
		},
	});

	useEffect(() => {
		// We need to clear existing comments data when we leave the space overview page
		return () => {
			resetInitialDataLoadedMap();
			clearCommentsData();
			clearProcessedData();
		};
	}, [clearCommentsData, resetInitialDataLoadedMap, clearProcessedData]);

	const { data } = spaceOverviewQueryResult;
	const spaceId = data?.space?.id || '';
	const spaceName = data?.space?.name || '';
	const isThemed = Boolean(data?.space?.theme?.themeKey);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && isThemed) {
		return null;
	}

	if (isRovoSystemSpaceKey(spaceKey)) {
		return null;
	}

	// The feature Company Hub uses a special space type "system" which is mostly
	// hidden from the UI and the user predominantly sees the space homepage /
	// overview presented with feature-specific customizations and referred to as
	// "front cover" or "landing page". Because the space is hidden from the user,
	// the UI concept "space icon" doesn't make sense.
	const spaceIconPath = isCompanyHub ? undefined : data?.space?.icon?.path;
	const emoji = getEmojiFromPublishedPageProperties(data?.space?.homepage ?? null);

	const contentHeaderProps: ComponentProps<typeof CompanyHubGroupedButtons> = {
		contentId,
		spaceKey,
		spaceOverviewQueryResult,
		versionOverride,
	};
	let contentHeader;
	if (isCompanyHub) {
		contentHeader = null;
	} else {
		contentHeader = (
			<FixedSpaceHeaderContainer isThemed={isThemed}>
				<ExperienceStart
					id={contentId}
					name={OBJECT_HEADER_EXPERIENCE}
					attributes={{ isSpaceOverview: true }}
				/>
				<ContentTypesHeader contentId={contentId} isSpaceOverviewPage />
			</FixedSpaceHeaderContainer>
		);
	}

	const spaceOverviewInternal = (
		<RendererActionsProvider>
			<>
				<StartSpaceOverviewPageLoad spaceKey={spaceKey} />
				{isCompanyHub && <CompanyHubGroupedButtons {...contentHeaderProps} />}
				<ViewPageContainer
					spaceKey={spaceKey}
					contentId={contentId}
					stickyHeader={contentHeader}
					isContainerContentView={!isCompanyHub}
					versionOverride={isCompanyHub ? versionOverride : null}
				>
					{({ contentQueryData, contentQueryError, isThemed }) => {
						const isFabricPage = Boolean(
							contentQueryData?.content?.nodes?.[0]?.body?.dynamic?.representation ===
							'atlas_doc_format',
						);

						const contentStatus = contentQueryData?.content?.nodes?.[0]?.status;

						// Space overview renders <ViewPageContent> directly (not ViewPageContentWithStatus),
						// so we derive isArchived from the content query and pass it down ourselves.
						const isArchived =
							contentStatus && fg('cc_comments_parse_is_archived_on_space_overview')
								? contentStatus === 'archived'
								: undefined;

						const shouldRenderInlineComments = !isCompanyHub;

						const shouldRenderInlineCommentsHighlighterLoader =
							shouldRenderInlineComments && !isFabricPage;

						const {
							coverPicture,
							contentAppearance,
							hasCoverPicture,
							coverPictureWidth,
							isTitleCenterAligned,
						} = getRendererCoverPictureInfo(contentQueryData, false);
						const isPageContentFullWidth = contentAppearance?.published === 'full-width';
						const hasEmoji = !!emoji || !!spaceIconPath;
						const isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji =
							!isTitleCenterAligned &&
							isFixedWidthImageOption &&
							isPageContentFullWidth &&
							hasCoverPicture;

						return (
							<>
								{/* Body of content */}
								{/* Space Overview NVP non-theme uses contentHeader inside of ViewPageContainer.
									Because contentHeader is disabled when themes are enabled, need to display the
									header outside of content to match Space Overview Classic
								*/}
								{!isCompanyHub && <RecommendedSpaceBanner spaceKey={spaceKey} />}
								{isThemed && contentHeader}
								{!isCompanyHub && (
									<CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.HEADER} />
								)}
								{!isCompanyHub && (
									<DocumentContentTitle
										contentTitle={contentQueryData?.content?.nodes?.[0]?.title || ''}
										contentSpace={spaceName}
									/>
								)}
								<ContentBodyStyledComponent
									isCompanyHub={isCompanyHub}
									data-testid="space-overview-content"
								>
									{hasCoverPicture && !isCompanyHub && (
										<RendererPageCoverPicture
											contentId={contentId}
											isPagePreview={false}
											coverPicture={coverPicture}
											contentAppearance={contentAppearance}
											isSpaceOverview
											hasEmoji={hasEmoji}
											coverPictureWidthOverride={coverPictureWidth}
										/>
									)}
									{!isCompanyHub && (
										<SpaceHeading
											{...{
												emoji,
												title: contentQueryData?.content?.nodes?.[0]?.title,
												isTitleCenterAligned,
												isCompanyHub,
												contentAppearance: contentAppearance?.published,
												hasCoverPicture,
												spaceIconPath,
												isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji,
												isPageContentFullWidth,
												isFixedWidthImageOption,
												cloudId,
												spaceId,
												spaceKey,
												orgId,
											}}
										/>
									)}
									{shouldRenderInlineCommentsHighlighterLoader && (
										<InlineCommentsHighlighterLoader pageId={contentId} />
									)}
									{isReadingAidsAutoHighlightEnabled && (
										<ReadingAidsAcronymsHighlighter contentId={contentId} />
									)}
									<DocumentUpdateStatusProvider>
										<ContentRefetchProvider>
											<AnnotationsProvider>
												<ViewPageContent
													contentId={contentId}
													data={contentQueryData}
													error={contentQueryError}
													spaceKey={spaceKey}
													hasInlineComments={shouldRenderInlineComments}
													isArchived={isArchived}
													isSpaceOverview
													customPerfData={{
														webPanelsAtlGeneral: panelsModuleKeysAtlGeneral,
														webPanelsAtlFooter: panelsModuleKeysAtlFooter,
													}}
												/>
											</AnnotationsProvider>
										</ContentRefetchProvider>
									</DocumentUpdateStatusProvider>
									{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
									{!process.env.REACT_SSR && (
										<Fragment>
											<ContentViewedEvent contentId={contentId} />
											<CommentsDataBackgroundLoader
												key={`CommentsDataBackgroundLoader-${contentId}`}
												contentId={contentId}
											/>
										</Fragment>
									)}
									{!isCompanyHub && (
										<CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.FOOTER} />
									)}
									<QuickReload
										contentId={contentId}
										contentType={contentQueryData?.content?.nodes?.[0]?.type}
										// The 'Date.now()' is expedient; see notes in quick-reload
										// package on what a more correct lastPollTime value would be.
										lastPollTime={Date.now()}
										pageMode={PageMode.RENDERER}
										showCommentsPanel={showCommentsPanel}
									/>
									{children}
									<TwgExplorerSectionLoader cloudId={cloudId} spaceId={spaceId} />
								</ContentBodyStyledComponent>
								{shouldRenderInlineComments && shouldDisplaySidebar && (
									<InlineCommentsLoader
										pageId={contentId}
										isFabricPage={isFabricPage}
										loadingPriority={
											window.__SSR_RENDERED__ && focusedCommentId
												? LoadingPriority.AFTER_PAINT
												: LoadingPriority.BACKGROUND
										}
										focusedCommentId={focusedCommentId}
									/>
								)}
								<ViewCommentShortcutListener isFabricPage={isFabricPage} contentId={contentId} />
								{!isCompanyHub && (
									<ObjectSidebarControl
										hasCommentsButton
										hasDetailsButton
										contentId={contentId}
										spaceKey={spaceKey}
										hasAiButton
									/>
								)}
								{isCompanyHub && versionOverride && (
									<HubPreviewFooter versionOverride={versionOverride} contentId={contentId} />
								)}
							</>
						);
					}}
				</ViewPageContainer>
			</>
		</RendererActionsProvider>
	);

	let spaceOverviewWithWrappers = spaceOverviewInternal;

	if (isCompanyHub) {
		spaceOverviewWithWrappers = (
			<HubRendererWrapper>
				<AudienceSegmentationContextProvider isCompanyHub shouldFetchUserGroups>
					{spaceOverviewWithWrappers}
				</AudienceSegmentationContextProvider>
			</HubRendererWrapper>
		);
	}

	return spaceOverviewWithWrappers;
};
